/**
 * Enhanced Blog Functionality
 * Mejoras para la experiencia del usuario en el blog
 */

class EnhancedBlog {
  constructor() {
    this.init();
  }

  init() {
    this.setupLazyLoading();
    this.setupAnimations();
    this.setupReadingProgress();
    this.setupSmoothScrolling();
  }

  // Configurar lazy loading para las imágenes
  setupLazyLoading() {
    if ('loading' in HTMLImageElement.prototype) {
      // El navegador soporta lazy loading nativo
      return;
    }

    // Fallback para navegadores que no soportan lazy loading
    const images = document.querySelectorAll('.card__media img[loading="lazy"]');
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src || img.src;
            img.classList.remove('lazyload');
            observer.unobserve(img);
          }
        });
      });

      images.forEach(img => imageObserver.observe(img));
    }
  }

  // Configurar animaciones de aparición
  setupAnimations() {
    if ('IntersectionObserver' in window) {
      const animationObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      // Observar elementos del blog
      const elementsToAnimate = document.querySelectorAll('.card-wrapper, .main-blog__header');
      elementsToAnimate.forEach(el => {
        animationObserver.observe(el);
      });
    }
  }

  // Configurar barra de progreso de lectura para artículos individuales
  setupReadingProgress() {
    if (!document.querySelector('.article-content')) return;

    const progressBar = document.createElement('div');
    progressBar.className = 'reading-progress';
    progressBar.innerHTML = '<div class="reading-progress__bar"></div>';
    document.body.appendChild(progressBar);

    const updateProgress = () => {
      const article = document.querySelector('.article-content');
      if (!article) return;

      const articleTop = article.offsetTop;
      const articleHeight = article.offsetHeight;
      const windowHeight = window.innerHeight;
      const scrollTop = window.pageYOffset;

      const progress = Math.max(0, Math.min(1, 
        (scrollTop - articleTop + windowHeight) / (articleHeight + windowHeight)
      ));

      progressBar.style.setProperty('--progress', `${progress * 100}%`);
    };

    window.addEventListener('scroll', updateProgress, { passive: true });
    updateProgress();
  }

  // Configurar scroll suave para la paginación
  setupSmoothScrolling() {
    const paginationLinks = document.querySelectorAll('.enhanced-pagination a');
    
    paginationLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        // Si es una navegación en la misma página, hacer scroll suave al top
        if (link.href.includes(window.location.pathname)) {
          e.preventDefault();
          
          // Mostrar indicador de carga
          this.showLoadingIndicator();
          
          // Simular navegación después de un breve delay
          setTimeout(() => {
            window.location.href = link.href;
          }, 300);
        }
      });
    });
  }

  // Mostrar indicador de carga
  showLoadingIndicator() {
    const indicator = document.createElement('div');
    indicator.className = 'loading-indicator';
    indicator.innerHTML = `
      <div class="loading-spinner"></div>
      <p>Cargando artículos...</p>
    `;
    document.body.appendChild(indicator);

    // Remover después de la navegación
    setTimeout(() => {
      if (indicator.parentNode) {
        indicator.parentNode.removeChild(indicator);
      }
    }, 2000);
  }

  // Utilidades para mejorar la accesibilidad
  static enhanceAccessibility() {
    // Mejorar navegación por teclado
    const cards = document.querySelectorAll('.card-wrapper');
    
    cards.forEach(card => {
      const link = card.querySelector('.card__heading a');
      if (link) {
        // Hacer toda la tarjeta clickeable
        card.addEventListener('click', (e) => {
          if (e.target.tagName !== 'A' && e.target.tagName !== 'BUTTON') {
            link.click();
          }
        });

        // Mejorar navegación por teclado
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            link.click();
          }
        });

        // Hacer la tarjeta focuseable
        card.setAttribute('tabindex', '0');
        card.setAttribute('role', 'button');
        card.setAttribute('aria-label', `Leer artículo: ${link.textContent.trim()}`);
      }
    });
  }
}

// CSS para las nuevas funcionalidades
const enhancedBlogStyles = `
  .animate-in {
    animation: fadeInUp 0.8s ease forwards;
  }

  .reading-progress {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 0.4rem;
    background: rgba(var(--color-border), 0.2);
    z-index: 1000;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .reading-progress.active {
    opacity: 1;
  }

  .reading-progress__bar {
    height: 100%;
    background: linear-gradient(90deg, rgb(var(--color-accent)), rgba(var(--color-accent), 0.8));
    width: var(--progress, 0%);
    transition: width 0.1s ease;
  }

  .loading-indicator {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: white;
    padding: 3rem;
    border-radius: 1.5rem;
    box-shadow: 0 2rem 4rem rgba(0, 0, 0, 0.15);
    z-index: 10000;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }

  .loading-spinner {
    width: 4rem;
    height: 4rem;
    border: 0.3rem solid rgba(var(--color-accent), 0.1);
    border-left: 0.3rem solid rgb(var(--color-accent));
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .loading-indicator p {
    margin: 0;
    color: rgb(var(--color-foreground));
    font-weight: 500;
  }

  /* Mejorar hover y focus en las tarjetas */
  .card-wrapper:focus {
    outline: 0.2rem solid rgb(var(--color-accent));
    outline-offset: 0.2rem;
  }

  .card-wrapper:focus:not(:focus-visible) {
    outline: none;
  }

  /* Animaciones de carga escalonada */
  .blog-articles .card-wrapper {
    opacity: 0;
    transform: translateY(2rem);
  }

  .blog-articles .card-wrapper.animate-in {
    opacity: 1;
    transform: translateY(0);
  }

  /* Preloader para imágenes */
  .card__media {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    position: relative;
  }

  .card__media img.lazyload {
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .card__media img.lazyloaded {
    opacity: 1;
  }
`;

// Inyectar estilos
const styleSheet = document.createElement('style');
styleSheet.textContent = enhancedBlogStyles;
document.head.appendChild(styleSheet);

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new EnhancedBlog();
    EnhancedBlog.enhanceAccessibility();
  });
} else {
  new EnhancedBlog();
  EnhancedBlog.enhanceAccessibility();
}
